/**
 * The one and only contact surface with `window.Telegram.WebApp`.
 *
 * Nothing else in `apps/web` may touch the global. Two properties make that worth
 * enforcing:
 *
 * 1. **It has to work with no Telegram at all.** Opening the built app in a plain browser
 *    must produce a working, developable UI, so {@link resolveWebApp} falls back to
 *    {@link createDevWebApp} — a cooperative in-memory shim that reads
 *    `#tgWebAppVersion` / `#tgWebAppPlatform` / `#tgWebAppData` out of `location.hash`.
 *    Detection is by feature (`typeof wa.ready === 'function'`), never by user agent.
 *
 * 2. **Every capability is version-gated.** The Mini Apps API is one object whose members
 *    appear over time, and an older client either throws `WebAppMethodUnsupported` or —
 *    worse, since Bot API 10.2's same-origin enforcement — silently no-ops through
 *    `postEvent`'s `{notAvailable: true}` branch with nothing thrown. So every recent
 *    method is behind {@link TelegramFacade.isVersionAtLeast} *and* a `typeof` check, and
 *    each gated call reports what happened by returning a boolean or a discriminated
 *    outcome instead of throwing. Gating is **never** a float comparison of the version
 *    string, which reads `7.10` as `7.1` — less than `7.7` — and so silently disables
 *    `setBottomBarColor` on every client that has it; {@link compareVersions} compares
 *    dotted integer segments instead.
 *
 * Art direction (docs/design-direction.md §10.1): the app is a dark art-directed object
 * and deliberately ignores the client's theme palette for every surface, text, border and
 * accent value. The one thing we take from Telegram is chrome colour *assignment*, not
 * colour *values* — {@link CHROME_GROUND} is written into the header, background and
 * bottom bar and re-asserted on `themeChanged`, and the theme is never read back.
 *
 * Four capabilities are deliberately absent, and its test asserts the module never so
 * much as names them: the client theme object; the Telegram-styled main button (it covers
 * the mini-player and all our actions are in-page); the data-and-close call (it closes the
 * Mini App, which kills playback instantly); and the two share helpers that only
 * third-party SDKs define, which throw `TypeError` inside a real client. So is every
 * viewport-height CSS unit.
 */

/* ────────────────────────────── public value types ────────────────────────────── */

/**
 * The literal page ground (`--ink-000`) of the shipped skin, stamped into Telegram's
 * chrome when the live ground cannot be read.
 *
 * It is a fallback rather than the value now, because the ground moves with the chosen
 * skin (`styles/skins.css`): a ПОЛНОЧЬ user with `#0B0A09` in the header gets a warm
 * black bar above a navy page, which is the one seam a Mini App cannot hide.
 * {@link readGround} resolves the live value; this constant is what a non-DOM
 * environment and a computed style that comes back empty both fall back to.
 */
export const CHROME_GROUND = '#0B0A09';

/**
 * The current `--bg`, trimmed, or {@link CHROME_GROUND}.
 *
 * `--bg` is the §3.2 alias every surface already uses, so it is correct for whichever
 * skin is applied, and it is a plain hex in every skin — Telegram's `setHeaderColor`
 * accepts `#RRGGBB` and nothing else, so a token that ever resolved to `rgb(...)` or a
 * `color-mix()` would have to be normalised here. It does not, and `skins.css` says so.
 */
export function readGround(root?: HTMLElement): string {
  const element = root ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (element === null) return CHROME_GROUND;
  try {
    const value = getComputedStyle(element).getPropertyValue('--bg').trim();
    return /^#[0-9a-fA-F]{6}$/.test(value) ? value : CHROME_GROUND;
  } catch {
    return CHROME_GROUND;
  }
}

/** Where the `WebApp` object came from. `'dev'` means the in-browser shim. */
export type TelegramSource = 'telegram' | 'dev';

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotification = 'error' | 'success' | 'warning';
export type HomeScreenStatus = 'unsupported' | 'unknown' | 'added' | 'missed';
export type InlineChatType = 'users' | 'bots' | 'groups' | 'channels';

/** Removes a subscription. Calling it more than once is safe. */
export type Unsubscribe = () => void;

export interface Insets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/** Everything the layout needs to size itself, in one snapshot. */
export interface ViewportMetrics {
  /** `viewportStableHeight || innerHeight`, in px — the value behind `--app-h`. */
  readonly height: number;
  /** `viewportHeight`, which jitters mid-drag. Exposed for diagnostics only. */
  readonly unstableHeight: number;
  readonly isExpanded: boolean;
  readonly isFullscreen: boolean;
  /** Device insets as Telegram reports them (8.0+); zeroes on older clients. */
  readonly safeArea: Insets;
  /** Insets imposed by Telegram's own chrome (8.0+); zeroes on older clients. */
  readonly contentSafeArea: Insets;
}

export type FullscreenOutcome =
  { readonly ok: true } | { readonly ok: false; readonly error: string };

/** All zeroes — the value used whenever the client cannot report insets. */
export const ZERO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/* ─────────────────────── the subset of WebApp we type at all ─────────────────────── */

/**
 * Telegram's storage callbacks are `(error, result)` with `error === null` on success.
 * Some clients hand back an `Error` instead of a string, so the type is `unknown` and
 * {@link toMessage} normalises it.
 */
export type StorageCallback<T> = (error: unknown, result?: T) => void;

export type MiniAppEventHandler = (payload?: unknown) => void;

export interface MiniAppBackButton {
  readonly isVisible?: boolean;
  show(): void;
  hide(): void;
  onClick(handler: () => void): void;
  offClick(handler: () => void): void;
}

export interface MiniAppHapticFeedback {
  impactOccurred?(style: HapticStyle): void;
  notificationOccurred?(type: HapticNotification): void;
  selectionChanged?(): void;
}

export interface MiniAppCloudStorage {
  setItem(key: string, value: string, callback?: StorageCallback<boolean>): void;
  getItem(key: string, callback: StorageCallback<string>): void;
  getItems(keys: readonly string[], callback: StorageCallback<Record<string, string>>): void;
  removeItem(key: string, callback?: StorageCallback<boolean>): void;
  removeItems(keys: readonly string[], callback?: StorageCallback<boolean>): void;
  getKeys(callback: StorageCallback<string[]>): void;
}

export interface MiniAppDeviceStorage {
  setItem(key: string, value: string, callback?: StorageCallback<boolean>): void;
  getItem(key: string, callback: StorageCallback<string>): void;
  removeItem(key: string, callback?: StorageCallback<boolean>): void;
  clear(callback?: StorageCallback<boolean>): void;
}

/**
 * `start_param` is the **only** member of `initDataUnsafe` this app reads. It travels
 * inside the signed query string, so a server-validated `start_param` is tamper-proof;
 * the unsigned `tgWebAppStartParam` GET parameter is not and is never consulted
 * (spec.md §14.4). Identity comes from the server's verification of the raw
 * {@link TelegramFacade.initData}, never from this object.
 */
export interface MiniAppInitDataUnsafe {
  readonly start_param?: string;
}

/** The members of `window.Telegram.WebApp` this app is allowed to know about. */
export interface MiniAppWebApp {
  readonly version: string;
  readonly platform: string;
  readonly initData: string;
  readonly initDataUnsafe?: MiniAppInitDataUnsafe;
  readonly viewportHeight: number;
  readonly viewportStableHeight: number;
  readonly isExpanded?: boolean;
  readonly isActive?: boolean;
  readonly isFullscreen?: boolean;
  readonly safeAreaInset?: Insets;
  readonly contentSafeAreaInset?: Insets;
  readonly BackButton?: MiniAppBackButton;
  readonly HapticFeedback?: MiniAppHapticFeedback;
  readonly CloudStorage?: MiniAppCloudStorage;
  readonly DeviceStorage?: MiniAppDeviceStorage;
  isVersionAtLeast?(version: string): boolean;
  ready(): void;
  expand(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  disableVerticalSwipes?(): void;
  enableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  checkHomeScreenStatus?(callback: (status: unknown) => void): void;
  addToHomeScreen?(): void;
  shareMessage?(preparedMessageId: string, callback?: (sent: unknown) => void): void;
  switchInlineQuery?(query: string, chatTypes?: readonly InlineChatType[]): void;
  openTelegramLink?(url: string): void;
  onEvent(event: string, handler: MiniAppEventHandler): void;
  offEvent(event: string, handler: MiniAppEventHandler): void;
}

interface WindowWithTelegram {
  readonly Telegram?: { readonly WebApp?: unknown };
}

/* ────────────────────────────── the façade contract ────────────────────────────── */

export interface BackButtonApi {
  /** `true` when the client is 6.1+ and exposes `BackButton`. */
  readonly available: boolean;
  readonly isVisible: boolean;
  show(): boolean;
  hide(): boolean;
  /** Registers a handler and returns its remover. A no-op when unavailable. */
  onClick(handler: () => void): Unsubscribe;
}

export interface HapticsApi {
  /** `true` when the client is 6.1+ and exposes `HapticFeedback`. */
  readonly available: boolean;
  impact(style: HapticStyle): void;
  notification(type: HapticNotification): void;
  selection(): void;
}

/**
 * `CloudStorage` (6.9+), promisified once at this boundary.
 *
 * An **unavailable** tier is not an error — it resolves to `null` / `false` / `{}` / `[]`,
 * because this is a cache tier and the server is the source of truth (spec.md §13.7). A
 * failure the client actually *reports*, and a key or value that breaks Telegram's
 * documented limits, both reject with an `Error`.
 */
export interface CloudStorageAdapter {
  readonly available: boolean;
  getItem(key: string): Promise<string | null>;
  getItems(keys: readonly string[]): Promise<Record<string, string>>;
  setItem(key: string, value: string): Promise<boolean>;
  removeItem(key: string): Promise<boolean>;
  removeItems(keys: readonly string[]): Promise<boolean>;
  getKeys(): Promise<string[]>;
}

/** `DeviceStorage` (9.0+), 5 MB and device-local. Same error contract as the cloud tier. */
export interface DeviceStorageAdapter {
  readonly available: boolean;
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<boolean>;
  removeItem(key: string): Promise<boolean>;
  clear(): Promise<boolean>;
}

export interface TelegramFacade {
  readonly source: TelegramSource;
  /** `false` when this is the dev shim — the flag every "open me in Telegram" guard uses. */
  readonly isTelegram: boolean;
  readonly version: string;
  readonly platform: string;
  /** The raw, signed `initData` query string. Verification happens on the server. */
  readonly initData: string;
  /** `initDataUnsafe.start_param`, or `null`. Never the unsigned GET parameter. */
  readonly startParam: string | null;

  isVersionAtLeast(version: string): boolean;

  ready(): void;
  expand(): void;
  /** Writes {@link CHROME_GROUND} into Telegram's chrome and keeps re-asserting it. */
  claimChrome(): void;
  /** Writes `--app-h` (and the safe-area custom properties) and keeps them in sync. */
  syncViewport(): void;

  /** 7.7+. Required, or a scrub/disc drag fights Telegram's sheet-dismiss gesture. */
  disableVerticalSwipes(): boolean;
  enableVerticalSwipes(): boolean;
  /** 6.2+. A stray swipe must not silently end a listening session. */
  enableClosingConfirmation(): boolean;
  disableClosingConfirmation(): boolean;

  viewport(): ViewportMetrics;
  onViewportChanged(handler: (metrics: ViewportMetrics) => void): Unsubscribe;

  readonly backButton: BackButtonApi;
  readonly haptics: HapticsApi;

  /** 8.0+. `true` on older clients, which have no concept of an inactive Mini App. */
  isActive(): boolean;
  /**
   * 8.0+. For state persistence and UI re-sync **only** — a handler here must never
   * touch the audio element (spec.md §13.3).
   */
  onActivated(handler: () => void): Unsubscribe;
  onDeactivated(handler: () => void): Unsubscribe;

  /** 8.0+. Tier one of the share ladder; resolves `false` when it did not happen. */
  shareMessage(preparedMessageId: string): Promise<boolean>;
  /** 6.7+. Tier two. Returns `false` on `WebAppInlineModeDisabled`. */
  switchInlineQuery(query: string, chatTypes?: readonly InlineChatType[]): boolean;
  /** Tier three, and the only way to open a `t.me` link from inside the Mini App. */
  openTelegramLink(url: string): boolean;

  /** 8.0+. Settles on `fullscreenChanged` / `fullscreenFailed`, never hangs. */
  requestFullscreen(): Promise<FullscreenOutcome>;
  exitFullscreen(): boolean;
  isFullscreen(): boolean;
  onFullscreenChanged(handler: (isFullscreen: boolean) => void): Unsubscribe;

  /** 8.0+. Resolves `'unsupported'` on an older client rather than throwing. */
  checkHomeScreenStatus(): Promise<HomeScreenStatus>;
  addToHomeScreen(): boolean;

  readonly cloudStorage: CloudStorageAdapter;
  readonly deviceStorage: DeviceStorageAdapter;
}

/* ──────────────────────────────── version gating ──────────────────────────────── */

/** The Mini Apps SDK's own default when it is not running inside Telegram. */
export const FALLBACK_VERSION = '6.0';

const V_BACK_BUTTON = '6.1';
const V_HAPTICS = '6.1';
const V_CLOSING_CONFIRMATION = '6.2';
const V_SWITCH_INLINE_QUERY = '6.7';
const V_CLOUD_STORAGE = '6.9';
const V_VERTICAL_SWIPES = '7.7';
const V_BOTTOM_BAR_COLOR = '7.10';
/**
 * Bot API 8.0 is the feature baseline for the modern shell: `isActive`,
 * `activated`/`deactivated`, `requestFullscreen`, `safeAreaInset`,
 * `contentSafeAreaInset`, `addToHomeScreen`, `shareMessage`.
 */
const V_MODERN = '8.0';
const V_DEVICE_STORAGE = '9.0';

/** How long a fullscreen / home-screen round trip may stay unanswered before we give up. */
const REQUEST_TIMEOUT_MS = 4000;

/** Telegram's documented `CloudStorage` limits. Exceeding either fails silently there. */
const STORAGE_KEY_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const CLOUD_VALUE_MAX_CHARS = 4096;

function segment(part: string | undefined): number {
  if (part === undefined) return 0;
  const value = Number.parseInt(part, 10);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Compares two dotted version strings segment by segment as integers.
 *
 * This exists so no gate is ever written as a float comparison: `7.10` as a number is
 * `7.1`, which is **less** than `7.7`, so a float gate silently disables
 * `setBottomBarColor` on every client that has it.
 *
 * @returns `-1`, `0` or `1` in the manner of a comparator.
 */
export function compareVersions(a: string, b: string): number {
  const left = a.split('.');
  const right = b.split('.');
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const x = segment(left[i]);
    const y = segment(right[i]);
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/* ──────────────────────────────── small helpers ──────────────────────────────── */

function noop(): void {
  /* nothing to undo */
}

/**
 * Runs a Mini Apps call that may throw `WebAppMethodUnsupported` on an older client.
 *
 * @returns `true` when the call completed, `false` when the client rejected it.
 */
function attempt(run: () => void): boolean {
  try {
    run();
    return true;
  } catch {
    return false;
  }
}

function toMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (typeof value === 'number') return `${value}`;
  return 'Telegram WebApp reported an unspecified error';
}

function isReportedError(value: unknown): boolean {
  return value !== null && value !== undefined && value !== false && value !== '';
}

function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function readBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function readInsets(raw: unknown): Insets {
  if (raw === null || typeof raw !== 'object') return ZERO_INSETS;
  const record = raw as { top?: unknown; right?: unknown; bottom?: unknown; left?: unknown };
  return {
    top: readNumber(record.top),
    right: readNumber(record.right),
    bottom: readNumber(record.bottom),
    left: readNumber(record.left),
  };
}

/** Pulls `payload.error` out of a Mini Apps failure event, e.g. `fullscreenFailed`. */
function readEventError(payload: unknown, fallback: string): string {
  if (payload !== null && typeof payload === 'object') {
    const record = payload as { error?: unknown };
    if (typeof record.error === 'string' && record.error.length > 0) return record.error;
  }
  return fallback;
}

function normalizeHomeScreenStatus(raw: unknown): HomeScreenStatus {
  return raw === 'added' || raw === 'missed' || raw === 'unknown' || raw === 'unsupported'
    ? raw
    : 'unknown';
}

/** Wraps one callback-style Mini Apps storage call as a promise. */
function promisify<T>(invoke: (callback: StorageCallback<T>) => void): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve, reject) => {
    let settled = false;
    const settle = (error: unknown, result?: T): void => {
      if (settled) return;
      settled = true;
      if (isReportedError(error)) {
        reject(new Error(toMessage(error)));
        return;
      }
      resolve(result);
    };
    try {
      invoke((error, result) => {
        settle(error, result);
      });
    } catch (cause) {
      settle(cause instanceof Error ? cause : new Error(toMessage(cause)));
    }
  });
}

function assertStorageKey(key: string): void {
  if (!STORAGE_KEY_PATTERN.test(key)) {
    throw new Error(
      `Telegram storage keys must match [A-Za-z0-9_-]{1,128}; refusing to write "${key}"`,
    );
  }
}

function assertCloudValue(key: string, value: string): void {
  if (value.length > CLOUD_VALUE_MAX_CHARS) {
    throw new Error(
      `CloudStorage values are capped at ${CLOUD_VALUE_MAX_CHARS} characters; "${key}" is ${value.length}`,
    );
  }
}

/* ─────────────────────────────── the façade itself ─────────────────────────────── */

/**
 * Wraps one `WebApp`-shaped object. Exported so tests (and T54's recording double) can
 * drive the façade without a global; application code uses {@link getTelegram} or
 * {@link telegram}.
 */
export function createTelegram(
  webApp: MiniAppWebApp,
  source: TelegramSource = 'telegram',
): TelegramFacade {
  const version = (): string => readString(webApp.version, FALLBACK_VERSION);

  const atLeast = (wanted: string): boolean => {
    if (typeof webApp.isVersionAtLeast === 'function') {
      try {
        return webApp.isVersionAtLeast(wanted) === true;
      } catch {
        /* an older client can throw here; fall through to our own comparison */
      }
    }
    return compareVersions(version(), wanted) >= 0;
  };

  const subscribe = (event: string, handler: MiniAppEventHandler): Unsubscribe => {
    if (!attempt(() => webApp.onEvent(event, handler))) return noop;
    let removed = false;
    return () => {
      if (removed) return;
      removed = true;
      attempt(() => webApp.offEvent(event, handler));
    };
  };

  /* ── chrome ─────────────────────────────────────────────────────────────────── */

  /**
   * The single allowlisted contact with Telegram's theme APIs (enforced by the
   * `bl/no-theme-params` ESLint rule): we *write* our ground into the chrome, and the
   * client's own palette is never read back in any form.
   */
  function reassertChrome(): void {
    /* Re-read on every call, not once at module load: `claimChrome()` is re-fired on
       Telegram's own `themeChanged` AND by the skin picker, so this is where a repaint
       reaches the header, the background and the bottom bar. */
    const ground = readGround();
    attempt(() => webApp.setHeaderColor(ground));
    attempt(() => webApp.setBackgroundColor(ground));
    if (atLeast(V_BOTTOM_BAR_COLOR)) {
      attempt(() => webApp.setBottomBarColor?.(ground));
    }
  }

  let chromeListenerInstalled = false;

  /* ── viewport ───────────────────────────────────────────────────────────────── */

  const windowHeight = (): number =>
    typeof window === 'undefined' ? 0 : readNumber(window.innerHeight);

  const stableHeight = (): number => {
    const stable = readNumber(webApp.viewportStableHeight);
    return stable > 0 ? stable : windowHeight();
  };

  const metrics = (): ViewportMetrics => {
    const modern = atLeast(V_MODERN);
    return {
      height: stableHeight(),
      unstableHeight: readNumber(webApp.viewportHeight),
      isExpanded: readBool(webApp.isExpanded, false),
      isFullscreen: modern && readBool(webApp.isFullscreen, false),
      safeArea: modern ? readInsets(webApp.safeAreaInset) : ZERO_INSETS,
      contentSafeArea: modern ? readInsets(webApp.contentSafeAreaInset) : ZERO_INSETS,
    };
  };

  /**
   * Writes the geometry Telegram owns into custom properties.
   *
   * `--app-h` comes from `viewportStableHeight` and nothing else: a viewport-height CSS
   * unit includes the strip under Telegram's own header (so the mini-player's bottom edge
   * becomes unreachable), and `viewportHeight` jitters during the expand animation and
   * shears the layout mid-drag.
   *
   * `--sa-*` / `--csa-*` are Telegram's reported device and content insets. They are
   * *additional* to CSS `env(safe-area-inset-*)`, which stays the right tool for the
   * 100px bottom chrome; the content insets are the only way to clear Telegram's own
   * header in fullscreen.
   */
  const writeViewportProperties = (): void => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const snapshot = metrics();
    root.style.setProperty('--app-h', `${snapshot.height}px`);
    root.style.setProperty('--sa-top', `${snapshot.safeArea.top}px`);
    root.style.setProperty('--sa-right', `${snapshot.safeArea.right}px`);
    root.style.setProperty('--sa-bottom', `${snapshot.safeArea.bottom}px`);
    root.style.setProperty('--sa-left', `${snapshot.safeArea.left}px`);
    root.style.setProperty('--csa-top', `${snapshot.contentSafeArea.top}px`);
    root.style.setProperty('--csa-right', `${snapshot.contentSafeArea.right}px`);
    root.style.setProperty('--csa-bottom', `${snapshot.contentSafeArea.bottom}px`);
    root.style.setProperty('--csa-left', `${snapshot.contentSafeArea.left}px`);
  };

  /** The geometry events worth re-reading on. The 8.0 ones are added only if gated in. */
  const geometryEvents = (): readonly string[] =>
    atLeast(V_MODERN)
      ? ['viewportChanged', 'safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged']
      : ['viewportChanged'];

  let viewportListenersInstalled = false;

  /* ── storage tiers ──────────────────────────────────────────────────────────── */

  const cloudApi = (): MiniAppCloudStorage | null => {
    const api = webApp.CloudStorage;
    return atLeast(V_CLOUD_STORAGE) && api !== undefined ? api : null;
  };

  const deviceApi = (): MiniAppDeviceStorage | null => {
    const api = webApp.DeviceStorage;
    return atLeast(V_DEVICE_STORAGE) && api !== undefined ? api : null;
  };

  const cloudStorage: CloudStorageAdapter = {
    get available(): boolean {
      return cloudApi() !== null;
    },
    async getItem(key: string): Promise<string | null> {
      const api = cloudApi();
      if (api === null) return null;
      assertStorageKey(key);
      const value = await promisify<string>((callback) => {
        api.getItem(key, callback);
      });
      // Telegram answers a missing key with an empty string, not with an error.
      return typeof value === 'string' && value.length > 0 ? value : null;
    },
    async getItems(keys: readonly string[]): Promise<Record<string, string>> {
      const api = cloudApi();
      if (api === null || keys.length === 0) return {};
      for (const key of keys) assertStorageKey(key);
      const value = await promisify<Record<string, string>>((callback) => {
        api.getItems(keys, callback);
      });
      return value !== null && typeof value === 'object' ? value : {};
    },
    async setItem(key: string, value: string): Promise<boolean> {
      const api = cloudApi();
      if (api === null) return false;
      assertStorageKey(key);
      assertCloudValue(key, value);
      const stored = await promisify<boolean>((callback) => {
        api.setItem(key, value, callback);
      });
      return stored !== false;
    },
    async removeItem(key: string): Promise<boolean> {
      const api = cloudApi();
      if (api === null) return false;
      assertStorageKey(key);
      const removed = await promisify<boolean>((callback) => {
        api.removeItem(key, callback);
      });
      return removed !== false;
    },
    async removeItems(keys: readonly string[]): Promise<boolean> {
      const api = cloudApi();
      if (api === null) return false;
      if (keys.length === 0) return true;
      for (const key of keys) assertStorageKey(key);
      const removed = await promisify<boolean>((callback) => {
        api.removeItems(keys, callback);
      });
      return removed !== false;
    },
    async getKeys(): Promise<string[]> {
      const api = cloudApi();
      if (api === null) return [];
      const keys = await promisify<string[]>((callback) => {
        api.getKeys(callback);
      });
      return Array.isArray(keys) ? keys : [];
    },
  };

  const deviceStorage: DeviceStorageAdapter = {
    get available(): boolean {
      return deviceApi() !== null;
    },
    async getItem(key: string): Promise<string | null> {
      const api = deviceApi();
      if (api === null) return null;
      assertStorageKey(key);
      const value = await promisify<string>((callback) => {
        api.getItem(key, callback);
      });
      return typeof value === 'string' && value.length > 0 ? value : null;
    },
    async setItem(key: string, value: string): Promise<boolean> {
      const api = deviceApi();
      if (api === null) return false;
      assertStorageKey(key);
      const stored = await promisify<boolean>((callback) => {
        api.setItem(key, value, callback);
      });
      return stored !== false;
    },
    async removeItem(key: string): Promise<boolean> {
      const api = deviceApi();
      if (api === null) return false;
      assertStorageKey(key);
      const removed = await promisify<boolean>((callback) => {
        api.removeItem(key, callback);
      });
      return removed !== false;
    },
    async clear(): Promise<boolean> {
      const api = deviceApi();
      if (api === null) return false;
      const cleared = await promisify<boolean>((callback) => {
        api.clear(callback);
      });
      return cleared !== false;
    },
  };

  /* ── back button and haptics ────────────────────────────────────────────────── */

  const backApi = (): MiniAppBackButton | null => {
    const api = webApp.BackButton;
    return atLeast(V_BACK_BUTTON) && api !== undefined ? api : null;
  };

  const backButton: BackButtonApi = {
    get available(): boolean {
      return backApi() !== null;
    },
    get isVisible(): boolean {
      const api = backApi();
      return api !== null && readBool(api.isVisible, false);
    },
    show(): boolean {
      const api = backApi();
      return api !== null && attempt(() => api.show());
    },
    hide(): boolean {
      const api = backApi();
      return api !== null && attempt(() => api.hide());
    },
    onClick(handler: () => void): Unsubscribe {
      const api = backApi();
      if (api === null) return noop;
      if (!attempt(() => api.onClick(handler))) return noop;
      let removed = false;
      return () => {
        if (removed) return;
        removed = true;
        attempt(() => api.offClick(handler));
      };
    },
  };

  /**
   * Haptics are absent on every desktop client and on old mobile ones, and a client that
   * does have them still throws `WebAppHapticFeedbackParamInvalid` for a style it does not
   * know. A missing buzz must never be able to break a row press, so all three are
   * gated, optional-chained and swallowed.
   */
  const haptics: HapticsApi = {
    get available(): boolean {
      return atLeast(V_HAPTICS) && webApp.HapticFeedback !== undefined;
    },
    impact(style: HapticStyle): void {
      if (!atLeast(V_HAPTICS)) return;
      attempt(() => webApp.HapticFeedback?.impactOccurred?.(style));
    },
    notification(type: HapticNotification): void {
      if (!atLeast(V_HAPTICS)) return;
      attempt(() => webApp.HapticFeedback?.notificationOccurred?.(type));
    },
    selection(): void {
      if (!atLeast(V_HAPTICS)) return;
      attempt(() => webApp.HapticFeedback?.selectionChanged?.());
    },
  };

  /* ── the object ─────────────────────────────────────────────────────────────── */

  const facade: TelegramFacade = {
    source,
    get isTelegram(): boolean {
      return source === 'telegram';
    },
    get version(): string {
      return version();
    },
    get platform(): string {
      return readString(webApp.platform, 'unknown');
    },
    get initData(): string {
      return readString(webApp.initData, '');
    },
    get startParam(): string | null {
      const raw: unknown = webApp.initDataUnsafe?.start_param;
      return typeof raw === 'string' && raw.length > 0 ? raw : null;
    },

    isVersionAtLeast: atLeast,

    ready(): void {
      attempt(() => webApp.ready());
    },
    expand(): void {
      attempt(() => webApp.expand());
    },

    claimChrome(): void {
      reassertChrome();
      if (chromeListenerInstalled) return;
      chromeListenerInstalled = true;
      // Re-assert on every theme change. We never read the new theme back — the app is
      // dark-only and a light-theme user gets exactly the same crate.
      subscribe('themeChanged', reassertChrome);
    },

    syncViewport(): void {
      writeViewportProperties();
      if (viewportListenersInstalled) return;
      viewportListenersInstalled = true;
      for (const event of geometryEvents()) subscribe(event, writeViewportProperties);
    },

    disableVerticalSwipes(): boolean {
      if (!atLeast(V_VERTICAL_SWIPES)) return false;
      return attempt(() => webApp.disableVerticalSwipes?.());
    },
    enableVerticalSwipes(): boolean {
      if (!atLeast(V_VERTICAL_SWIPES)) return false;
      return attempt(() => webApp.enableVerticalSwipes?.());
    },
    enableClosingConfirmation(): boolean {
      if (!atLeast(V_CLOSING_CONFIRMATION)) return false;
      return attempt(() => webApp.enableClosingConfirmation?.());
    },
    disableClosingConfirmation(): boolean {
      if (!atLeast(V_CLOSING_CONFIRMATION)) return false;
      return attempt(() => webApp.disableClosingConfirmation?.());
    },

    viewport: metrics,
    onViewportChanged(handler: (snapshot: ViewportMetrics) => void): Unsubscribe {
      const relay: MiniAppEventHandler = () => {
        handler(metrics());
      };
      const offs = geometryEvents().map((event) => subscribe(event, relay));
      return () => {
        for (const off of offs) off();
      };
    },

    backButton,
    haptics,

    isActive(): boolean {
      if (!atLeast(V_MODERN)) return true;
      return readBool(webApp.isActive, true);
    },
    onActivated(handler: () => void): Unsubscribe {
      if (!atLeast(V_MODERN)) return noop;
      return subscribe('activated', () => {
        handler();
      });
    },
    onDeactivated(handler: () => void): Unsubscribe {
      if (!atLeast(V_MODERN)) return noop;
      return subscribe('deactivated', () => {
        handler();
      });
    },

    shareMessage(preparedMessageId: string): Promise<boolean> {
      if (!atLeast(V_MODERN) || typeof webApp.shareMessage !== 'function') {
        return Promise.resolve(false);
      }
      return new Promise<boolean>((resolve) => {
        let settled = false;
        const settle = (sent: boolean): void => {
          if (settled) return;
          settled = true;
          resolve(sent);
        };
        // A user who dismisses the share sheet is not an error: `sent === false`.
        if (
          !attempt(() => {
            webApp.shareMessage?.(preparedMessageId, (sent: unknown) => {
              settle(sent === true);
            });
          })
        ) {
          settle(false);
        }
      });
    },

    switchInlineQuery(query: string, chatTypes?: readonly InlineChatType[]): boolean {
      if (!atLeast(V_SWITCH_INLINE_QUERY) || typeof webApp.switchInlineQuery !== 'function') {
        return false;
      }
      try {
        // Throws `WebAppInlineModeDisabled` unless inline mode is on for the bot, which
        // is a BotFather setting we cannot detect — so the ladder falls through instead.
        if (chatTypes === undefined) webApp.switchInlineQuery(query);
        else webApp.switchInlineQuery(query, chatTypes);
        return true;
      } catch {
        return false;
      }
    },

    openTelegramLink(url: string): boolean {
      if (typeof webApp.openTelegramLink !== 'function') return false;
      return attempt(() => webApp.openTelegramLink?.(url));
    },

    requestFullscreen(): Promise<FullscreenOutcome> {
      if (!atLeast(V_MODERN) || typeof webApp.requestFullscreen !== 'function') {
        return Promise.resolve({ ok: false, error: 'UNSUPPORTED' });
      }
      if (readBool(webApp.isFullscreen, false)) return Promise.resolve({ ok: true });

      return new Promise<FullscreenOutcome>((resolve) => {
        let settled = false;
        /** Both handlers must be removed either way, or the next request settles early. */
        const settle = (outcome: FullscreenOutcome): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          attempt(() => webApp.offEvent('fullscreenChanged', onChanged));
          attempt(() => webApp.offEvent('fullscreenFailed', onFailed));
          resolve(outcome);
        };
        function onChanged(): void {
          settle({ ok: true });
        }
        function onFailed(payload?: unknown): void {
          settle({ ok: false, error: readEventError(payload, 'UNSUPPORTED') });
        }

        // Created before anything can settle, so `settle` always has it to clear.
        // Desktop clients answer `fullscreenFailed` with `UNSUPPORTED`; a client that
        // answers nothing at all must not leave the caller awaiting forever.
        const timer = setTimeout(() => {
          settle({ ok: false, error: 'TIMEOUT' });
        }, REQUEST_TIMEOUT_MS);

        attempt(() => webApp.onEvent('fullscreenChanged', onChanged));
        attempt(() => webApp.onEvent('fullscreenFailed', onFailed));

        if (!attempt(() => webApp.requestFullscreen?.())) {
          settle({ ok: false, error: 'UNSUPPORTED' });
        }
      });
    },

    exitFullscreen(): boolean {
      if (!atLeast(V_MODERN)) return false;
      return attempt(() => webApp.exitFullscreen?.());
    },
    isFullscreen(): boolean {
      return atLeast(V_MODERN) && readBool(webApp.isFullscreen, false);
    },
    onFullscreenChanged(handler: (isFullscreen: boolean) => void): Unsubscribe {
      if (!atLeast(V_MODERN)) return noop;
      return subscribe('fullscreenChanged', () => {
        handler(readBool(webApp.isFullscreen, false));
      });
    },

    checkHomeScreenStatus(): Promise<HomeScreenStatus> {
      if (!atLeast(V_MODERN) || typeof webApp.checkHomeScreenStatus !== 'function') {
        return Promise.resolve('unsupported');
      }
      return new Promise<HomeScreenStatus>((resolve) => {
        let settled = false;
        const settle = (status: HomeScreenStatus): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(status);
        };
        const timer = setTimeout(() => {
          settle('unknown');
        }, REQUEST_TIMEOUT_MS);
        if (
          !attempt(() => {
            webApp.checkHomeScreenStatus?.((status: unknown) => {
              settle(normalizeHomeScreenStatus(status));
            });
          })
        ) {
          settle('unsupported');
        }
      });
    },

    addToHomeScreen(): boolean {
      if (!atLeast(V_MODERN)) return false;
      return attempt(() => webApp.addToHomeScreen?.());
    },

    cloudStorage,
    deviceStorage,
  };

  return facade;
}

/* ──────────────────────────────── the dev shim ──────────────────────────────── */

export interface DevWebAppOptions {
  /** Overrides `location.hash`; accepted with or without the leading `#`. */
  hash?: string;
  version?: string;
  platform?: string;
  /** A raw `initData` query string, as `tgWebAppData` would carry. */
  initData?: string;
  /** What `checkHomeScreenStatus` reports. `'missed'` keeps that path developable. */
  homeScreenStatus?: HomeScreenStatus;
}

function readLocationHash(): string {
  if (typeof window === 'undefined') return '';
  const hash: unknown = window.location.hash;
  return typeof hash === 'string' ? hash : '';
}

function parseHashParams(hash: string): URLSearchParams {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  try {
    return new URLSearchParams(raw);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * `start_param` is read out of the (would-be signed) `initData` string, exactly as the
 * real client does — never out of a separate hash parameter, so the dev shim cannot
 * accidentally make an unsigned deep-link payload look trustworthy.
 */
function parseInitDataUnsafe(initData: string): MiniAppInitDataUnsafe {
  if (initData.length === 0) return {};
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(initData);
  } catch {
    return {};
  }
  const startParam = params.get('start_param');
  return startParam !== null && startParam.length > 0 ? { start_param: startParam } : {};
}

function createMapStorage(): MiniAppCloudStorage & MiniAppDeviceStorage {
  const store = new Map<string, string>();
  return {
    setItem(key, value, callback) {
      store.set(key, value);
      queueMicrotask(() => callback?.(null, true));
    },
    getItem(key, callback) {
      const value = store.get(key) ?? '';
      queueMicrotask(() => callback(null, value));
    },
    getItems(keys, callback) {
      const out: Record<string, string> = {};
      for (const key of keys) out[key] = store.get(key) ?? '';
      queueMicrotask(() => callback(null, out));
    },
    removeItem(key, callback) {
      store.delete(key);
      queueMicrotask(() => callback?.(null, true));
    },
    removeItems(keys, callback) {
      for (const key of keys) store.delete(key);
      queueMicrotask(() => callback?.(null, true));
    },
    getKeys(callback) {
      const keys = [...store.keys()];
      queueMicrotask(() => callback(null, keys));
    },
    clear(callback) {
      store.clear();
      queueMicrotask(() => callback?.(null, true));
    },
  };
}

/**
 * A cooperative `WebApp` double for running the app in a plain browser.
 *
 * It is a **development harness**, not a fidelity model of any real client: the version
 * and platform come from the URL hash (`#tgWebAppVersion=8.0&tgWebAppPlatform=android`)
 * precisely so the 7.x/8.x paths can be exercised outside Telegram, where the real SDK
 * would report `6.0` and every gate would be dead code locally. Deliberate fidelity
 * choices: `switchInlineQuery` throws `WebAppInlineModeDisabled` (inline mode genuinely
 * is not available here, so the share ladder falls through as it would in production),
 * the storage tiers are in-memory so nothing survives a reload, and `viewportChanged` is
 * bridged from `window`'s `resize` so `--app-h` tracks the browser window.
 */
export function createDevWebApp(options: DevWebAppOptions = {}): MiniAppWebApp {
  const params = parseHashParams(options.hash ?? readLocationHash());
  const version = options.version ?? params.get('tgWebAppVersion') ?? FALLBACK_VERSION;
  const platform = options.platform ?? params.get('tgWebAppPlatform') ?? 'unknown';
  const initData = options.initData ?? params.get('tgWebAppData') ?? '';
  const initDataUnsafe = parseInitDataUnsafe(initData);
  const homeScreenStatus = options.homeScreenStatus ?? 'missed';

  const listeners = new Map<string, Set<MiniAppEventHandler>>();
  const emit = (event: string, payload?: unknown): void => {
    const handlers = listeners.get(event);
    if (handlers === undefined) return;
    for (const handler of [...handlers]) attempt(() => handler(payload));
  };

  const storage = createMapStorage();
  let expanded = false;
  let fullscreen = false;
  let bridged = false;

  const bridgeResize = (): void => {
    if (bridged || typeof window === 'undefined') return;
    bridged = true;
    window.addEventListener('resize', () => {
      emit('viewportChanged', { isStateStable: true });
    });
  };

  const shim: MiniAppWebApp = {
    version,
    platform,
    initData,
    initDataUnsafe,
    get viewportHeight(): number {
      return typeof window === 'undefined' ? 0 : window.innerHeight;
    },
    get viewportStableHeight(): number {
      return typeof window === 'undefined' ? 0 : window.innerHeight;
    },
    get isExpanded(): boolean {
      return expanded;
    },
    isActive: true,
    get isFullscreen(): boolean {
      return fullscreen;
    },
    safeAreaInset: ZERO_INSETS,
    contentSafeAreaInset: ZERO_INSETS,
    BackButton: (() => {
      const clicks = new Set<() => void>();
      let visible = false;
      return {
        get isVisible(): boolean {
          return visible;
        },
        show(): void {
          visible = true;
        },
        hide(): void {
          visible = false;
        },
        onClick(handler: () => void): void {
          clicks.add(handler);
        },
        offClick(handler: () => void): void {
          clicks.delete(handler);
        },
      };
    })(),
    HapticFeedback: {
      impactOccurred(): void {
        /* no vibration motor in a browser tab */
      },
      notificationOccurred(): void {
        /* idem */
      },
      selectionChanged(): void {
        /* idem */
      },
    },
    CloudStorage: storage,
    DeviceStorage: storage,
    isVersionAtLeast(wanted: string): boolean {
      return compareVersions(version, wanted) >= 0;
    },
    ready(): void {
      /* there is no splash screen to dismiss outside Telegram */
    },
    expand(): void {
      expanded = true;
    },
    setHeaderColor(): void {
      /* no chrome to colour */
    },
    setBackgroundColor(): void {
      /* idem */
    },
    setBottomBarColor(): void {
      /* idem */
    },
    disableVerticalSwipes(): void {
      /* there is no sheet-dismiss gesture to fight */
    },
    enableVerticalSwipes(): void {
      /* idem */
    },
    enableClosingConfirmation(): void {
      /* a browser tab has its own close confirmation story */
    },
    disableClosingConfirmation(): void {
      /* idem */
    },
    requestFullscreen(): void {
      fullscreen = true;
      queueMicrotask(() => {
        emit('fullscreenChanged', { isFullscreen: true });
      });
    },
    exitFullscreen(): void {
      fullscreen = false;
      queueMicrotask(() => {
        emit('fullscreenChanged', { isFullscreen: false });
      });
    },
    checkHomeScreenStatus(callback: (status: unknown) => void): void {
      queueMicrotask(() => callback(homeScreenStatus));
    },
    addToHomeScreen(): void {
      queueMicrotask(() => emit('homeScreenAdded'));
    },
    shareMessage(_preparedMessageId: string, callback?: (sent: unknown) => void): void {
      queueMicrotask(() => callback?.(true));
    },
    switchInlineQuery(): void {
      // Faithful: inline mode is a BotFather setting and is not available here, so the
      // share ladder must fall through to `openTelegramLink` exactly as it would live.
      throw new Error('WebAppInlineModeDisabled');
    },
    openTelegramLink(): void {
      /* recorded by nothing: opening a t.me link is a no-op in the harness */
    },
    onEvent(event: string, handler: MiniAppEventHandler): void {
      let handlers = listeners.get(event);
      if (handlers === undefined) {
        handlers = new Set<MiniAppEventHandler>();
        listeners.set(event, handlers);
      }
      handlers.add(handler);
      if (event === 'viewportChanged') bridgeResize();
    },
    offEvent(event: string, handler: MiniAppEventHandler): void {
      listeners.get(event)?.delete(handler);
    },
  };

  return shim;
}

/* ──────────────────────────── resolution and singleton ──────────────────────────── */

/**
 * Returns the real `window.Telegram.WebApp`, or `null` when the app is not running
 * inside Telegram. Feature-detected: the object must at least look like the SDK, because
 * a half-injected global is worse than none.
 */
export function readGlobalWebApp(): MiniAppWebApp | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as unknown as WindowWithTelegram).Telegram?.WebApp;
  if (candidate === null || typeof candidate !== 'object') return null;
  const webApp = candidate as Partial<MiniAppWebApp>;
  if (typeof webApp.ready !== 'function') return null;
  if (typeof webApp.onEvent !== 'function') return null;
  if (typeof webApp.version !== 'string') return null;
  return webApp as MiniAppWebApp;
}

/** The real client if there is one, otherwise the dev shim. Never throws, never null. */
export function resolveWebApp(options?: DevWebAppOptions): {
  webApp: MiniAppWebApp;
  source: TelegramSource;
} {
  const real = readGlobalWebApp();
  if (real !== null) return { webApp: real, source: 'telegram' };
  return { webApp: createDevWebApp(options ?? {}), source: 'dev' };
}

let memo: TelegramFacade | null = null;

/** The process-wide façade, resolved (and memoised) on first use. */
export function getTelegram(): TelegramFacade {
  if (memo === null) {
    const resolved = resolveWebApp();
    memo = createTelegram(resolved.webApp, resolved.source);
  }
  return memo;
}

/** Drops the memo so the next {@link getTelegram} re-resolves. For HMR and tests. */
export function resetTelegram(): void {
  memo = null;
}

/**
 * The façade as a plain object, for `import { telegram } from '../lib/telegram'`.
 *
 * Every member forwards to {@link getTelegram}, so importing this module has no side
 * effects and touches no global until something is actually called — which is what lets a
 * test install its own `window.Telegram` after the import.
 */
export const telegram: TelegramFacade = {
  get source(): TelegramSource {
    return getTelegram().source;
  },
  get isTelegram(): boolean {
    return getTelegram().isTelegram;
  },
  get version(): string {
    return getTelegram().version;
  },
  get platform(): string {
    return getTelegram().platform;
  },
  get initData(): string {
    return getTelegram().initData;
  },
  get startParam(): string | null {
    return getTelegram().startParam;
  },
  isVersionAtLeast: (wanted) => getTelegram().isVersionAtLeast(wanted),
  ready: () => {
    getTelegram().ready();
  },
  expand: () => {
    getTelegram().expand();
  },
  claimChrome: () => {
    getTelegram().claimChrome();
  },
  syncViewport: () => {
    getTelegram().syncViewport();
  },
  disableVerticalSwipes: () => getTelegram().disableVerticalSwipes(),
  enableVerticalSwipes: () => getTelegram().enableVerticalSwipes(),
  enableClosingConfirmation: () => getTelegram().enableClosingConfirmation(),
  disableClosingConfirmation: () => getTelegram().disableClosingConfirmation(),
  viewport: () => getTelegram().viewport(),
  onViewportChanged: (handler) => getTelegram().onViewportChanged(handler),
  get backButton(): BackButtonApi {
    return getTelegram().backButton;
  },
  get haptics(): HapticsApi {
    return getTelegram().haptics;
  },
  isActive: () => getTelegram().isActive(),
  onActivated: (handler) => getTelegram().onActivated(handler),
  onDeactivated: (handler) => getTelegram().onDeactivated(handler),
  shareMessage: (preparedMessageId) => getTelegram().shareMessage(preparedMessageId),
  switchInlineQuery: (query, chatTypes) =>
    chatTypes === undefined
      ? getTelegram().switchInlineQuery(query)
      : getTelegram().switchInlineQuery(query, chatTypes),
  openTelegramLink: (url) => getTelegram().openTelegramLink(url),
  requestFullscreen: () => getTelegram().requestFullscreen(),
  exitFullscreen: () => getTelegram().exitFullscreen(),
  isFullscreen: () => getTelegram().isFullscreen(),
  onFullscreenChanged: (handler) => getTelegram().onFullscreenChanged(handler),
  checkHomeScreenStatus: () => getTelegram().checkHomeScreenStatus(),
  addToHomeScreen: () => getTelegram().addToHomeScreen(),
  get cloudStorage(): CloudStorageAdapter {
    return getTelegram().cloudStorage;
  },
  get deviceStorage(): DeviceStorageAdapter {
    return getTelegram().deviceStorage;
  },
};
